import { STAFF_IMPORT_JOB_SUBPATHS, STAFF_IMPORT_PATHS, staffImportAliasListSchema, staffImportFailureDetailsSchema, staffImportJobDetailSchema, staffImportJobPageSchema, staffImportJobPath, staffImportPreviewSchema, staffImportProgressSchema, staffImportReviewResultSchema, staffImportReviewRowDetailSchema, staffImportReviewRowPageSchema, staffImportRowPageSchema, staffImportRowPath, staffImportUploadTargetSchema, } from "@lafayette/schemas";
import { ApiHttpError, ApiNetworkError, ApiResponseError, ApiUploadError, NO_FAILURE_DETAIL, } from "./api-failure";
import { createJsonTransport } from "./json-transport";
import { staffCatalogSearchParams } from "./staff-catalog";
// --- errors -----------------------------------------------------------------------------------------
/**
 * The API answered with an error. `code` is the envelope's code (`conflict` for 409, `unprocessable`
 * for 422, `payload_too_large` for 413, `service_unavailable` for 503, ...). On a 422 from P1 or W2,
 * `parseErrors` says why the file was refused; on a 422 about a mapping, `mappingIssues` says which
 * columns or targets are wrong. Both hold codes and positions only. The UI renders the messages.
 */
export class StaffImportsApiError extends ApiHttpError {
    parseErrors;
    mappingIssues;
    /** CHT-708. On a 422 from W6 or W7: which rows the review refused. */
    reviewRefusals;
    /** CHT-708. On a 422 from W9: why the job cannot be approved. */
    approvalBlockers;
    constructor(status, code, message, requestId, details) {
        super(status, code, message, requestId);
        this.name = "StaffImportsApiError";
        this.parseErrors = details?.parseErrors ?? [];
        this.mappingIssues = details?.mappingIssues ?? [];
        this.reviewRefusals = details?.reviewRefusals ?? [];
        this.approvalBlockers = details?.approvalBlockers ?? [];
    }
    get detail() {
        return {
            ...NO_FAILURE_DETAIL,
            importRefusal: {
                parseErrors: this.parseErrors,
                mappingIssues: this.mappingIssues,
                reviewRefusals: this.reviewRefusals,
                approvalBlockers: this.approvalBlockers,
            },
        };
    }
}
/** The request never got an answer (no network, DNS, a closed connection) after every retry. */
export class StaffImportsNetworkError extends ApiNetworkError {
    constructor(options) {
        super("the import API could not be reached", options);
        this.name = "StaffImportsNetworkError";
    }
}
/** The API answered 2xx with a body that does not match the contract. */
export class StaffImportsResponseError extends ApiResponseError {
    constructor() {
        super("the import API answered with an unexpected body");
        this.name = "StaffImportsResponseError";
    }
}
/** The storage refused or could not take the file. `status` is the storage's HTTP status, or 0 when it never answered. */
export class StaffImportsUploadError extends ApiUploadError {
    constructor(status, options) {
        super(status, "the file could not be uploaded", options);
        this.name = "StaffImportsUploadError";
    }
}
export function createStaffImportsApi(options) {
    const send = createJsonTransport(options, {
        refusedMessage: "the import API refused the request",
        network: (cause) => new StaffImportsNetworkError({ cause }),
        response: () => new StaffImportsResponseError(),
        http: ({ status, code, message, requestId, body }) => {
            const details = staffImportFailureDetailsSchema.safeParse(body);
            return new StaffImportsApiError(status, code, message, requestId, details.success
                ? {
                    parseErrors: details.data.parseErrors,
                    mappingIssues: details.data.mappingIssues,
                    reviewRefusals: details.data.reviewRefusals,
                    approvalBlockers: details.data.approvalBlockers,
                }
                : undefined);
        },
    });
    const fetcher = () => options.fetch ?? globalThis.fetch.bind(globalThis);
    const read = (path, schema, query, callOptions) => send({
        method: "GET",
        path,
        schema,
        ...(query === undefined ? {} : { query: staffCatalogSearchParams(query) }),
        signal: callOptions?.signal,
    });
    const command = (path, schema, body, commandOptions) => send({
        method: "POST",
        path,
        schema,
        body,
        ...(commandOptions?.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: commandOptions.idempotencyKey }),
        signal: commandOptions?.signal,
    });
    const jobCommand = (jobId, subpath, body, commandOptions) => command(staffImportJobPath(jobId, subpath), staffImportJobDetailSchema, body, commandOptions);
    const sub = STAFF_IMPORT_JOB_SUBPATHS;
    return {
        createUploadTarget: (input, o) => command(STAFF_IMPORT_PATHS.uploads, staffImportUploadTargetSchema, input, o),
        async uploadFile(target, body, o) {
            let response;
            try {
                response = await fetcher()(target.url, {
                    method: target.method,
                    headers: target.headers,
                    body: body,
                    ...(o?.signal === undefined ? {} : { signal: o.signal }),
                });
            }
            catch (error) {
                if (o?.signal?.aborted === true)
                    throw error;
                throw new StaffImportsUploadError(0, { cause: error });
            }
            if (!response.ok)
                throw new StaffImportsUploadError(response.status);
        },
        previewFile: (input, o) => command(STAFF_IMPORT_PATHS.previews, staffImportPreviewSchema, input, o),
        createDraft: (input, o) => command(STAFF_IMPORT_PATHS.jobs, staffImportJobDetailSchema, input, o),
        saveMapping: (jobId, input, o) => jobCommand(jobId, sub.mapping, input, o),
        cancelJob: (jobId, input, o) => jobCommand(jobId, sub.cancel, input, o),
        submitMapping: (jobId, input, o) => jobCommand(jobId, sub.submit, input, o),
        listJobs: (query, o) => read(STAFF_IMPORT_PATHS.jobs, staffImportJobPageSchema, query, o),
        getJob: (jobId, o) => read(staffImportJobPath(jobId), staffImportJobDetailSchema, undefined, o),
        listRows: (jobId, query, o) => read(staffImportJobPath(jobId, sub.rows), staffImportRowPageSchema, query, o),
        listAliases: (query, o) => read(STAFF_IMPORT_PATHS.aliases, staffImportAliasListSchema, query, o),
        listReviewRows: (jobId, query, o) => read(staffImportJobPath(jobId, sub.reviewRows), staffImportReviewRowPageSchema, query, o),
        getReviewRow: (jobId, rowId, o) => read(staffImportRowPath(jobId, rowId), staffImportReviewRowDetailSchema, undefined, o),
        getProgress: (jobId, o) => read(staffImportJobPath(jobId, sub.progress), staffImportProgressSchema, undefined, o),
        recordDecision: (jobId, rowId, input, o) => command(staffImportRowPath(jobId, rowId, true), staffImportReviewRowDetailSchema, input, o),
        reviewRows: (jobId, input, o) => command(staffImportJobPath(jobId, sub.review), staffImportReviewResultSchema, input, o),
        bulkReview: (jobId, input, o) => command(staffImportJobPath(jobId, sub.bulkReview), staffImportReviewResultSchema, input, o),
        approveJob: (jobId, input, o) => jobCommand(jobId, sub.approve, input, o),
        retryJob: (jobId, input, o) => jobCommand(jobId, sub.retry, input, o),
    };
}
